import { zodResolver } from '@hookform/resolvers/zod';
import { useReducer, useState, useEffect } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { useInitContext } from '~/app/providers/InitProvider/hooks/useInitContext';
import {
  PaymentStep,
  useStepContext,
} from '~/app/providers/paymentStepProvider';
import { ErrorPage } from '~/pages/error';
import { PaymentError } from '~/shared/api/types/catalog';
import { useKeyboardNavigation } from '~/shared/hooks/useKeyboardNavigation';
import { PageLayout } from '~/shared/ui/PageLayout/PageLayout';
import { SnackBar } from '~/shared/ui/SnackBar/SnackBar';

import { DEFAULT_VALUES } from '../constants/defaultValues';
import { fieldName } from '../constants/paymentCard';
import { DEFAULT_CURRENCY } from '../features/Steam/utils/constants/defaultValues';
import { useGetCatalog } from '../hooks/useGetCatalog';
import { usePayOrder } from '../hooks/usePayOrder';
import { useSessionPolling } from '../hooks/useSessionPolling';
import { useLocalization } from '../localization/localization';

import { initialState, reducer } from './PayPage.reducer';
import { AmountType, FormType, useSchema } from './PayPage.scheme';
import { Form } from './PayPage.ui';
import { PaymentStep as PaymentStepComponent } from './components/steps/PaymentStep';
import { RedirectStep } from './components/steps/RedirectStep';
import { SelectCertificateStep } from './components/steps/SelectCertificateStep';
import { SelectProductStep } from './components/steps/SelectProductStep';
import { SteamStep } from './components/steps/SteamStep';

export const PayPage = () => {
  const [searchParams] = useSearchParams();
  const appId = searchParams.get('app_id');
  const [isPaymentLoading, setIsPaymentLoading] = useState(false);
  const [sessionData, setSessionData] = useState<{
    sessionId: string;
    hash: string;
  } | null>(null);
  const [redirectUrl, setRedirectUrl] = useState<string>('');
  const { currentStep, goToStep, goBack, canGoBack } = useStepContext();
  const [state, dispatch] = useReducer(reducer, initialState);

  const { closeMiniApp, error: initError } = useInitContext();
  const { schema } = useSchema(appId === 'ru');
  const [isErrorPrice, setIsErrorPrice] = useState(false);
  const { t } = useLocalization();

  const form = useForm<FormType>({
    defaultValues: DEFAULT_VALUES,
    mode: 'onTouched',
    resolver: zodResolver(schema),
    reValidateMode: 'onChange',
  });

  const {
    formState: { errors, isValid },
    handleSubmit,
    setError,
    setValue,
  } = form;

  const { isError } = useGetCatalog(initError);
  const { mutate: payOrder } = usePayOrder();

  const { startPolling } = useSessionPolling({
    hash: sessionData?.hash ?? '',
    sessionId: sessionData?.sessionId ?? '',
    onError: () => {
      dispatch({ type: 'UPDATE_SESSION', payload: { error: true } });
      setIsPaymentLoading(false);
    },
    onSessionError: () => {
      setIsPaymentLoading(false);
      closeMiniApp();
    },
    onSuccess: () => {
      dispatch({
        type: 'UPDATE_SESSION',
        payload: { isCompleteSession: true },
      });
      setIsPaymentLoading(false);
      closeMiniApp();
    },
    onRedirect: (url: string) => {
      setRedirectUrl(url);
      goToStep(PaymentStep.REDIRECT);
    },
  });

  useEffect(() => {
    if (sessionData) {
      startPolling();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionData]);

  const onSubmit: SubmitHandler<FormType> = async (data) => {
    setIsPaymentLoading(true);

    const paymentData =
      data?.product?.name === 'steam'
        ? {
            account: data.steamData!.login,
            productId: data.product!.id,
            amount: {
              majorValue: data.steamData!.amount.toString(),
              currency: DEFAULT_CURRENCY,
            },
            ...(data.promocode ? { promocode: data.promocode } : {}),
          }
        : {
            account: data.ruData![fieldName.EMAIL],
            productId: data.product!.id,
            amount: data.amount!,
          };
    if (appId === 'ru') {
      payOrder(paymentData, {
        onSuccess: (response) => {
          setSessionData({
            sessionId: response.sessionId,
            hash: response.hash,
          });
        },
        onError: (error) => {
          setIsPaymentLoading(false);
          const paymentError = error as PaymentError;

          if (paymentError?.response?.data?.type === 'amount_changed') {
            setIsErrorPrice(true);

            const newAmount = paymentError.response.data.amount as AmountType;

            if (newAmount) {
              setValue('amount', newAmount);
            }

            return;
          }

          console.error('Payment order failed:', error);
          setError('root', {
            type: 'server',
          });
        },
      });
    }
  };

  useKeyboardNavigation({
    onEscape: () => {
      if (canGoBack && currentStep !== PaymentStep.REDIRECT) {
        goBack();
      }
    },
    onEnter: () => {
      if (
        (currentStep === PaymentStep.PAYMENT ||
          currentStep === PaymentStep.STEAM) &&
        isValid &&
        !isPaymentLoading
      ) {
        handleSubmit(onSubmit)();
      }
    },
    enabled: currentStep !== PaymentStep.REDIRECT,
  });

  if (
    state.session.error ||
    initError ||
    (errors.root && !errors.root.binacle) ||
    isError
  ) {
    return <ErrorPage />;
  }

  const STEP_COMPONENTS: Record<PaymentStep, React.ReactNode> = {
    [PaymentStep.SELECT_CERTIFICATE]: <SelectCertificateStep />,
    [PaymentStep.SELECT_PRODUCT]: <SelectProductStep />,
    [PaymentStep.PAYMENT]: (
      <PaymentStepComponent appId={appId} isLoading={isPaymentLoading} />
    ),
    [PaymentStep.STEAM]: <SteamStep isLoading={isPaymentLoading} />,
    [PaymentStep.REDIRECT]: <RedirectStep url={redirectUrl} />,
  };

  return (
    <PageLayout>
      <SnackBar
        title={t('price changed error')}
        onClose={() => setIsErrorPrice(false)}
        open={isErrorPrice}
      />
      <FormProvider {...form}>
        <Form onSubmit={handleSubmit(onSubmit)}>
          {STEP_COMPONENTS[currentStep]}
        </Form>
      </FormProvider>
    </PageLayout>
  );
};
